import { ApiError } from './api';
import { SESSION_PROTOCOL_VERSION } from './session-core';

export interface FactoryStatus {
  enabled: boolean;
  configured: boolean;
  modelConfigured: boolean;
  pairSpecificEnabled: boolean;
  contentEngine?: {
    enabled: boolean;
    tavilyConfigured: boolean;
    globalQuestionTarget: number;
    budget: { monthKey: string; budgetUsd: number; estimatedSpentUsd: number; remainingUsd: number };
    visualCatalog: { configured: boolean; r2Configured: boolean; activeAssets: number; attachedVisualChoices: number; missingVisualChoices: number; targetAssets: number };
  };
}
export interface FactoryBatch {
  id: string; requestedCount: number; requestedDimension: string | null; requestedDepth: number | null;
  requestedRelationshipContext: string | null; locale: string; providerModel: string;
  status: 'generating'|'ready'|'failed'; candidateCount: number; acceptedCount: number; rejectedCount: number;
  errorCode: string | null; createdAt: string; completedAt: string | null;
}
export interface FactoryCandidate {
  id: string; batchId: string; conceptId: string; sequencePosition: number; family: string; dimension: string; depth: number;
  relationshipEligibility: string[]; locale: string; visualMode: boolean;
  optionASemantic: string; optionBSemantic: string; optionCSemantic: string; optionDSemantic: string;
  variants: {
    sameBrain: { question: string; optionA: string; optionB: string; optionC: string; optionD: string };
    readMe: { question: string; optionA: string; optionB: string; optionC: string; optionD: string };
    dontMatch: { question: string; optionA: string; optionB: string; optionC: string; optionD: string };
  };
  sourceModel: string; safetyStatus: 'passed'|'failed'; qualityStatus: 'passed'|'failed'; duplicateStatus: 'clear'|'duplicate';
  duplicateOfConceptId: string | null; reviewStatus: 'pending'|'approved'|'rejected'; rejectionReason: string | null; createdAt: string;
}

const headers = (initData: string): Record<string,string> => ({
  Accept: 'application/json', Authorization: `tma ${initData}`, 'Content-Type': 'application/json', 'X-UTAGE-Protocol': String(SESSION_PROTOCOL_VERSION)
});
const parse = async <T>(response: Response, fallback: string): Promise<T> => {
  const body = await response.json().catch(() => ({})) as T & { error?: { message?: string; code?: string } };
  if (!response.ok) throw new ApiError(body.error?.message ?? fallback, body.error?.code);
  return body;
};

export const loadFactoryStatus = async (initData:string):Promise<FactoryStatus> =>
  parse<FactoryStatus>(await fetch('/question-factory/status',{cache:'no-store',headers:headers(initData)}),'Could not load the question factory.');

export const loadFactoryCandidates = async (initData:string):Promise<FactoryCandidate[]> => {
  const body=await parse<{candidates:FactoryCandidate[]}>(await fetch('/question-factory/candidates',{cache:'no-store',headers:headers(initData)}),'Could not load factory candidates.');
  return body.candidates;
};

export const generateFactoryBatch = async (initData:string,input:{count:number;dimension?:string;depth?:number;relationshipContext?:string}):Promise<FactoryBatch> => {
  const response=await fetch('/question-factory/generate',{method:'POST',headers:headers(initData),body:JSON.stringify(input)});
  return (await parse<{batch:FactoryBatch}>(response,'Could not generate candidates.')).batch;
};

export const reviewFactoryCandidate = async (initData:string,candidateId:string,action:'approve'|'reject'):Promise<FactoryCandidate> => {
  const response=await fetch(`/question-factory/candidates/${encodeURIComponent(candidateId)}/review`,{
    method:'POST',headers:headers(initData),body:JSON.stringify({actionId:crypto.randomUUID(),action})
  });
  return (await parse<{candidate:FactoryCandidate}>(response,'Could not save that review.')).candidate;
};
